/**
 * camera.ts — third-person follow camera.
 *
 * Over-the-shoulder rather than first-person so swings, drags and footwork are
 * all visible while tuning. The camera reads the LOCAL look angles, not the
 * interpolated snapshot yaw, so aiming never feels like it lags behind the mouse.
 *
 * Those angles are the body's facing PLUS the free-look offset (`input.lookYaw` /
 * `lookPitch`), which is the only thing Alt moves. Nothing here knows or cares
 * whether free look is engaged: this function places a camera at whatever angles
 * it is handed.
 */

import * as THREE from 'three';
import { FEEL } from '@dueler/shared';

// Scratch vectors: this runs every frame and should not allocate.
const look = new THREE.Vector3();
const target = new THREE.Vector3();
const up = new THREE.Vector3(0, 1, 0);

/**
 * Places `camera` for the current view mode.
 *
 * First person puts the eye where the head is, which is the view that matters
 * for judging where your own blade actually is — the guard, the reach and the
 * arc all read differently from behind the shoulder. Third person stays for
 * watching your own swings while tuning.
 */
export function followCamera(
  camera: THREE.PerspectiveCamera,
  focus: THREE.Vector3,
  yaw: number,
  pitch: number,
  firstPerson = false,
): void {
  // Look direction for these angles. Yaw 0 faces -Z, matching the server.
  look.set(-Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), -Math.cos(yaw) * Math.cos(pitch));

  if (firstPerson) {
    // Eye height, just forward of the capsule axis so the body never clips the
    // near plane.
    camera.position
      .copy(focus)
      .addScaledVector(up, FEEL.EYE_HEIGHT - FEEL.BODY_HEIGHT / 2)
      .addScaledVector(look, 0.12);
    target.copy(camera.position).addScaledVector(look, 6);
    camera.lookAt(target);
    return;
  }

  const shoulder = FEEL.CAMERA_HEIGHT - FEEL.BODY_HEIGHT / 2;
  camera.position
    .copy(focus)
    .addScaledVector(look, -FEEL.CAMERA_DISTANCE)
    .addScaledVector(up, shoulder);

  target.copy(focus).addScaledVector(look, 6);
  target.y += shoulder;
  camera.lookAt(target);
}
